import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ChevronRight, CalendarDays, BookOpen } from 'lucide-react';
import { useBudgets, useBudgetItems, useCreateBudget, useDeleteBudget, useCreateBudgetItem, useDeleteBudgetItem, Budget } from '@/hooks/useBudgets';
import { useMasterBudgets } from '@/hooks/useMasterBudgets';
import { useMonthlyBudgets } from '@/hooks/useMonthlyBudgets';
import { useFetchCategories } from '@metadataHooks';
import { useTransactions } from '@/hooks/useTransactions';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CreateMasterBudgetDialog } from './components/CreateMasterBudgetDialog';
import { CreateMonthlyBudgetDialog } from './components/CreateMonthlyBudgetDialog';

function BudgetDetail({ budget, onDelete }: { budget: Budget; onDelete: (id: string) => void }) {
  const { data: items } = useBudgetItems(budget.id);
  const { data: categories } = useFetchCategories();
  const currentMonth = format(new Date(), 'yyyy-MM-dd');
  const { data: transactions } = useTransactions(
    budget.type === 'monthly' && budget.month
      ? { dateFrom: budget.month, dateTo: format(new Date(new Date(budget.month).getFullYear(), new Date(budget.month).getMonth() + 1, 0), 'yyyy-MM-dd') }
      : undefined
  );
  const createItem = useCreateBudgetItem();
  const deleteItem = useDeleteBudgetItem();
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newCategoryId, setNewCategoryId] = useState('');
  const [newLimit, setNewLimit] = useState('');

  const expenseCategories = (categories ?? []).filter(c => c.type === 'expense');
  const usedCategoryIds = new Set((items ?? []).map(i => i.category_id));

  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryId || !newLimit) return;
    try {
      await createItem.mutateAsync({ budget_id: budget.id, category_id: newCategoryId, limit_amount: parseFloat(newLimit) });
      setAddItemOpen(false);
      setNewCategoryId('');
      setNewLimit('');
      toast.success('Budget item added');
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const getSpent = (categoryId: string | null) => {
    if (!transactions) return 0;
    return transactions
      .filter(t => t.type === 'expense' && t.category_id === categoryId)
      .reduce((sum, t) => sum + Number(t.amount), 0);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>{budget.name}</CardTitle>
          <CardDescription>
            {budget.type === 'monthly' && budget.month ? format(new Date(budget.month), 'MMMM yyyy') : 'Ongoing'}
            {budget.total_limit ? ` · Limit: ${Number(budget.total_limit).toLocaleString()}` : ''}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setAddItemOpen(true)}>
            <Plus className="mr-1 h-3 w-3" /> Category
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="icon"><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete "{budget.name}"?</AlertDialogTitle>
                <AlertDialogDescription>This will delete the budget and all its items.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(budget.id)}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!items?.length ? (
          <p className="text-sm text-muted-foreground">No budget items yet. Add category limits to start tracking.</p>
        ) : (
          items.map(item => {
            const spent = getSpent(item.category_id);
            const limit = Number(item.limit_amount);
            const pct = limit > 0 ? Math.min((spent / limit) * 100, 100) : 0;
            const isOver = spent > limit;
            const isWarning = pct >= 80 && !isOver;
            return (
              <div key={item.id} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">
                    {item.categories?.icon} {item.categories?.name ?? 'Unknown'}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className={isOver ? 'text-destructive font-semibold' : isWarning ? 'text-amber-600 font-semibold' : 'text-muted-foreground'}>
                      {spent.toLocaleString()} / {limit.toLocaleString()}
                    </span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6"><Trash2 className="h-3 w-3" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Remove this budget item?</AlertDialogTitle></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => deleteItem.mutate(item.id)}>Remove</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <Progress
                  value={pct}
                  className={`h-2 ${isOver ? '[&>div]:bg-destructive' : isWarning ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`}
                />
              </div>
            );
          })
        )}

        <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Budget Category</DialogTitle></DialogHeader>
            <form onSubmit={handleAddItem} className="space-y-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newCategoryId} onValueChange={setNewCategoryId}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {expenseCategories
                      .filter(c => !usedCategoryIds.has(c.id))
                      .map(c => <SelectItem key={c.id} value={c.id}>{c.icon} {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Spending Limit</Label>
                <Input type="number" step="0.01" min="0" value={newLimit} onChange={e => setNewLimit(e.target.value)} required />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAddItemOpen(false)}>Cancel</Button>
                <Button type="submit">Add</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export default function Budgets() {
  const { data: masterBudgets } = useBudgets('master');
  const { data: monthlyBudgets } = useBudgets('monthly');
  const { data: newMasterBudgets, isLoading: loadingMaster } = useMasterBudgets();
  const { data: newMonthlyBudgets, isLoading: loadingMonthly } = useMonthlyBudgets();
  const createBudget = useCreateBudget();
  const deleteBudget = useDeleteBudget();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('master');
  const [newMonth, setNewMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [newTotalLimit, setNewTotalLimit] = useState('');

  const currentYear = new Date().getFullYear();

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await createBudget.mutateAsync({
        name: newName.trim(),
        type: newType,
        month: newType === 'monthly' ? `${newMonth}-01` : null,
        total_limit: newTotalLimit ? parseFloat(newTotalLimit) : null,
      });
      toast.success('Budget created');
      setCreateOpen(false);
      setNewName('');
      setNewTotalLimit('');
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteBudget.mutateAsync(id);
      toast.success('Budget deleted');
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Budgets</h1>
          <p className="text-muted-foreground">Set spending limits and track your progress.</p>
        </div>
      </div>

      {/* Master Budgets Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-xl font-semibold">Master Budgets</h2>
          </div>
          <CreateMasterBudgetDialog trigger={
            <Button size="sm"><Plus className="mr-2 h-4 w-4" /> New Master Budget</Button>
          } />
        </div>

        {loadingMaster ? (
          <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Loading...</p></CardContent></Card>
        ) : !newMasterBudgets?.length ? (
          <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">No master budgets yet. Create one to define your annual spending plan.</p></CardContent></Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {newMasterBudgets.map(budget => (
              <Link key={budget.id} to={`/budgets/master/${budget.id}`} className="block group">
                <Card className="transition-colors hover:border-primary/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base leading-tight">{budget.name}</CardTitle>
                      {budget.year > currentYear && (
                        <Badge variant="secondary" className="shrink-0 text-xs">Future</Badge>
                      )}
                    </div>
                    {budget.description && (
                      <CardDescription className="line-clamp-1">{budget.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="flex items-center justify-between pt-0">
                    <span className="text-sm text-muted-foreground">{budget.year}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Monthly Budgets Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-xl font-semibold">Monthly Budgets</h2>
          </div>
          <CreateMonthlyBudgetDialog trigger={
            <Button size="sm"><Plus className="mr-2 h-4 w-4" /> New Monthly Budget</Button>
          } />
        </div>

        {loadingMonthly ? (
          <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">Loading...</p></CardContent></Card>
        ) : !newMonthlyBudgets?.length ? (
          <Card><CardContent className="pt-6"><p className="text-sm text-muted-foreground">No monthly budgets yet. Create one from a master budget to track a specific month.</p></CardContent></Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {newMonthlyBudgets.map(budget => (
              <Link key={budget.id} to={`/budgets/monthly/${budget.id}`} className="block group">
                <Card className="transition-colors hover:border-primary/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base leading-tight">{budget.title}</CardTitle>
                    {budget.description && (
                      <CardDescription className="line-clamp-1">{budget.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="flex items-center justify-between pt-0">
                    <span className="text-sm text-muted-foreground">
                      {MONTH_NAMES[budget.month - 1]} {budget.year}
                    </span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
